import React, { useMemo, useState } from 'react';
import { Check, CircleDollarSign, Plus, X } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useConstruction } from '../../context/ConstructionContext';
import { paymentStatementAmounts, toIsoDate } from '../../construction/evm';
import { canDo } from '../../construction/permissions';
import { formatMoney } from '../../utils/helpers';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import Button from '../common/Button';
import ConstructionShell, { FormError, Panel, inputClass, labelClass, runAction } from './ConstructionShell';
import { t } from '../../i18n';

const EP_STATUS = {
  pendiente_pm: { label: 'Pendiente Jefe de Proyecto', tone: 'warning' },
  pendiente_director: { label: 'Pendiente Director de Proyectos', tone: 'warning' },
  aprobado: { label: 'Aprobado', tone: 'success' },
  pagado: { label: 'Pagado', tone: 'info' },
  rechazado: { label: 'Rechazado', tone: 'danger' }
};

const emptyContract = { id: null, supplierId: '', name: '', scope: '', contractAmount: '', retentionPct: 5, wbsItemIds: [], purchaseOrderId: '', startDate: '', endDate: '' };

const ContractsContent = ({ summary }) => {
  const { suppliers, purchaseOrders, currentUser } = useAppContext();
  const { state, can, convert, displayCurrency, upsertContractor, createPaymentStatement, advancePaymentStatement, userName } = useConstruction();
  const [contractForm, setContractForm] = useState(null);
  const [contractError, setContractError] = useState('');
  const [epForm, setEpForm] = useState({ contractorId: '', periodEnd: toIsoDate(new Date()), progressPct: '', note: '' });
  const [epError, setEpError] = useState('');
  const money = (amount) => formatMoney(Math.round(convert(amount || 0, summary.currency, displayCurrency)), displayCurrency);
  const role = currentUser?.role;

  const contracts = state.contractors.filter((contract) => contract.parkId === summary.parkId);
  const statements = state.paymentStatements.filter((statement) => statement.parkId === summary.parkId);
  const contractById = Object.fromEntries(contracts.map((contract) => [contract.id, contract]));
  const leaves = summary.evm.rows.filter((row) => row.isLeaf || row.depth === 0);
  const parkOrders = (purchaseOrders || []).filter((order) => order.parkId === summary.parkId);

  const contractRows = contracts.map((contract) => {
    const valid = statements.filter((statement) => statement.contractorId === contract.id && statement.status !== 'rechazado');
    const valued = valid.reduce((max, statement) => Math.max(max, Number(statement.progressPct) || 0), 0);
    const paid = valid.filter((statement) => statement.status === 'pagado').reduce((sum, statement) => sum + (Number(statement.netAmount) || 0), 0);
    const approved = valid.filter((statement) => ['aprobado', 'pagado'].includes(statement.status)).reduce((sum, statement) => sum + (Number(statement.grossAmount) || 0), 0);
    const physical = (contract.wbsItemIds || [])
      .map((itemId) => summary.evm.rows.find((row) => row.id === itemId))
      .filter(Boolean);
    const physicalPct = physical.length > 0 ? physical.reduce((sum, row) => sum + row.actual, 0) / physical.length : null;
    return { ...contract, valued, paid, approved, physicalPct };
  });

  const epPreview = useMemo(() => {
    const contract = contractById[epForm.contractorId];
    if (!contract || epForm.progressPct === '') return null;
    const previous = statements
      .filter((statement) => statement.contractorId === contract.id && statement.status !== 'rechazado')
      .reduce((max, statement) => Math.max(max, Number(statement.progressPct) || 0), 0);
    return { previous, ...paymentStatementAmounts(contract.contractAmount, epForm.progressPct, previous, contract.retentionPct) };
  }, [epForm, statements, contractById]);

  const nextActionFor = (statement) => {
    if (statement.status === 'pendiente_pm' && canDo(role, 'payments.approvePm')) return { label: 'Aprobar (Jefe de Proyecto)', reject: true };
    if (statement.status === 'pendiente_director' && canDo(role, 'payments.approveDirector')) return { label: 'Aprobar (Director)', reject: true };
    if (statement.status === 'aprobado' && canDo(role, 'payments.markPaid')) return { label: 'Marcar pagado', reject: false };
    return null;
  };

  const submitContract = (event) => {
    event.preventDefault();
    const saved = runAction(
      () => upsertContractor({ ...contractForm, parkId: summary.parkId, purchaseOrderId: contractForm.purchaseOrderId || null }),
      setContractError
    );
    if (saved) setContractForm(null);
  };

  const submitEp = (event) => {
    event.preventDefault();
    const saved = runAction(() => createPaymentStatement(epForm), setEpError);
    if (saved) setEpForm((prev) => ({ ...prev, progressPct: '', note: '' }));
  };

  const contractField = (key) => ({ value: contractForm?.[key] ?? '', onChange: (event) => setContractForm((prev) => ({ ...prev, [key]: event.target.value })) });

  return (
    <div className="space-y-4">
      <Panel
        title="Contratistas y subcontratos"
        description="Ligados a proveedores existentes. Dueño: Gerente de Contratos."
        actions={can('contracts.edit') && !contractForm ? <Button variant="primary" size="sm" icon={Plus} onClick={() => { setContractError(''); setContractForm({ ...emptyContract }); }}>{t('Nuevo contrato')}</Button> : null}
      >
        {contractForm && (
          <form onSubmit={submitContract} className="mb-4 border border-neutral-200 rounded p-3 bg-neutral-50 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>{t('Proveedor')}</label>
                <select
                  className={inputClass}
                  value={contractForm.supplierId}
                  onChange={(event) => {
                    const supplier = (suppliers || []).find((candidate) => candidate.id === event.target.value);
                    setContractForm((prev) => ({ ...prev, supplierId: event.target.value, name: prev.name || supplier?.name || '' }));
                  }}
                >
                  <option value="">{t('Seleccionar proveedor...')}</option>
                  {(suppliers || []).map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>{t('Nombre del contratista')}</label>
                <input className={inputClass} {...contractField('name')} />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Alcance')}</label>
                <input className={inputClass} {...contractField('scope')} />
              </div>
              <div>
                <label className={labelClass}>{t('Monto contrato ({0})', summary.currency)}</label>
                <input type="number" min="0" className={inputClass} {...contractField('contractAmount')} />
              </div>
              <div>
                <label className={labelClass}>{t('Retención (%)')}</label>
                <input type="number" min="0" max="20" step="0.5" className={inputClass} {...contractField('retentionPct')} />
              </div>
              <div>
                <label className={labelClass}>{t('Inicio')}</label>
                <input type="date" className={inputClass} {...contractField('startDate')} />
              </div>
              <div>
                <label className={labelClass}>{t('Término')}</label>
                <input type="date" className={inputClass} {...contractField('endDate')} />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Partidas del contrato')}</label>
                <select
                  multiple
                  className={`${inputClass} h-20`}
                  value={contractForm.wbsItemIds}
                  onChange={(event) => setContractForm((prev) => ({ ...prev, wbsItemIds: [...event.target.selectedOptions].map((option) => option.value) }))}
                >
                  {leaves.map((row) => <option key={row.id} value={row.id}>{`${row.code} ${row.name}`}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('OC asociada (evita contar dos veces el costo)')}</label>
                <select className={inputClass} {...contractField('purchaseOrderId')}>
                  <option value="">{t('Sin OC asociada')}</option>
                  {parkOrders.map((order) => <option key={order.id} value={order.id}>{`${order.id} · ${order.supplierName || ''}`}</option>)}
                </select>
              </div>
            </div>
            <FormError message={contractError} />
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm">{t('Guardar contrato')}</Button>
              <Button size="sm" onClick={() => setContractForm(null)}>{t('Cancelar')}</Button>
            </div>
          </form>
        )}
        <DataTable
          data={contractRows}
          columns={[
            { key: 'name', label: 'Contratista', sortable: true },
            { key: 'scope', label: 'Alcance', render: (value) => value || '—' },
            { key: 'contractAmount', label: 'Monto contrato', align: 'right', sortable: true, render: money },
            { key: 'physicalPct', label: 'Avance físico partidas', align: 'right', render: (value) => (value == null ? '—' : `${value.toFixed(1)}%`) },
            { key: 'valued', label: 'Valorizado', align: 'right', render: (value) => `${value.toFixed(1)}%` },
            { key: 'approved', label: 'Aprobado (bruto)', align: 'right', render: money },
            { key: 'paid', label: 'Pagado (neto)', align: 'right', render: money },
            { key: 'purchaseOrderId', label: 'OC', render: (value) => value || '—' },
            ...(can('contracts.edit')
              ? [{ key: 'id', label: 'Acciones', render: (value, row) => <Button size="sm" onClick={() => { setContractError(''); setContractForm({ ...emptyContract, ...row, purchaseOrderId: row.purchaseOrderId || '' }); }}>{t('Editar')}</Button> }]
              : [])
          ]}
          searchPlaceholder="Buscar contratista..."
          emptyTitle="Sin contratos"
          emptyDescription="El Gerente de Contratos da de alta los subcontratos de la obra."
        />
      </Panel>

      {can('payments.create') && contracts.length > 0 && (
        <Panel title="Emitir estado de pago" description="Valorización por avance: (% acumulado − % ya valorizado) × monto del contrato, menos retención.">
          <form onSubmit={submitEp} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2">
              <label className={labelClass}>{t('Contrato')}</label>
              <select className={inputClass} value={epForm.contractorId} onChange={(event) => setEpForm((prev) => ({ ...prev, contractorId: event.target.value }))}>
                <option value="">{t('Selecciona un contrato')}</option>
                {contracts.map((contract) => <option key={contract.id} value={contract.id}>{contract.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('Fin del período')}</label>
              <input type="date" className={inputClass} value={epForm.periodEnd} onChange={(event) => setEpForm((prev) => ({ ...prev, periodEnd: event.target.value }))} />
            </div>
            <div>
              <label className={labelClass}>{t('% avance acumulado aprobado')}</label>
              <input type="number" min="0" max="100" step="0.5" className={inputClass} value={epForm.progressPct} onChange={(event) => setEpForm((prev) => ({ ...prev, progressPct: event.target.value }))} />
            </div>
            <Button type="submit" variant="primary" size="sm">{t('Emitir EP')}</Button>
            {epPreview && (
              <p className="md:col-span-5 text-xs text-neutral-600">
                {t('Ya valorizado {0}% · Delta {1}% · Bruto {2} · Retención {3} · Neto a pagar {4}', epPreview.previous, epPreview.deltaPct, money(epPreview.grossAmount), money(epPreview.retention), money(epPreview.netAmount))}
              </p>
            )}
          </form>
          <div className="mt-2"><FormError message={epError} /></div>
        </Panel>
      )}

      <Panel title="Estados de pago" description="Flujo: Jefe de Proyecto → Director de Proyectos → aprobado → pagado. Los aprobados alimentan el costo real (AC).">
        <FormError message={epError} />
        <DataTable
          data={statements
            .sort((a, b) => (b.periodEnd || '').localeCompare(a.periodEnd || ''))
            .map((statement) => ({ ...statement, contractName: contractById[statement.contractorId]?.name || '—', statusLabel: EP_STATUS[statement.status]?.label || statement.status }))}
          columns={[
            { key: 'contractName', label: 'Contratista', sortable: true },
            { key: 'number', label: 'EP N°', align: 'right', sortable: true },
            { key: 'periodEnd', label: 'Período', sortable: true },
            { key: 'progressPct', label: '% acumulado', align: 'right', render: (value) => `${Number(value).toFixed(1)}%` },
            { key: 'deltaPct', label: '% del período', align: 'right', render: (value) => `${Number(value || 0).toFixed(1)}%` },
            { key: 'grossAmount', label: 'Bruto', align: 'right', render: money },
            { key: 'retention', label: 'Retención', align: 'right', render: money },
            { key: 'netAmount', label: 'Neto', align: 'right', render: money },
            { key: 'statusLabel', label: 'Estado', render: (value, row) => <Badge tone={EP_STATUS[row.status]?.tone}>{t(value)}</Badge> },
            {
              key: 'history',
              label: 'Último movimiento',
              render: (value) => {
                const last = (value || [])[(value || []).length - 1];
                return last ? `${userName(last.userId)} · ${last.at}` : '—';
              }
            },
            {
              key: 'id',
              label: 'Acciones',
              render: (value, row) => {
                const action = nextActionFor(row);
                if (!action) return '—';
                return (
                  <div className="flex gap-1">
                    <Button size="sm" icon={row.status === 'aprobado' ? CircleDollarSign : Check} onClick={() => runAction(() => advancePaymentStatement(value, 'approve'), setEpError)}>{t(action.label)}</Button>
                    {action.reject && <Button size="sm" variant="danger" icon={X} onClick={() => runAction(() => advancePaymentStatement(value, 'reject'), setEpError)}>{t('Rechazar')}</Button>}
                  </div>
                );
              }
            }
          ]}
          searchPlaceholder="Buscar estado de pago..."
          emptyTitle="Sin estados de pago"
          emptyDescription="Emite el primer estado de pago de un contrato."
        />
      </Panel>
    </div>
  );
};

const ConstructionContracts = () => (
  <ConstructionShell title="Contratistas y estados de pago" description="Subcontratos de la obra y valorizaciones por avance con flujo de aprobación.">
    {(summary) => <ContractsContent summary={summary} />}
  </ConstructionShell>
);

export default ConstructionContracts;
